// ============================================================================
// bundle widget — UNIFIED drop-in JS for BOTH stores (Luxora + elife/Dawn).
// The same file adapts to either theme via feature detection; nothing needs
// editing per-store.
//
// Structure (top → bottom):
//   1. Shared preamble  — locale strings, loading spinner, reveal gate. Spinner
//                         anchors to `.product__subtitle` (Luxora) or, absent
//                         that, `.product__title` (elife).
//   2. Bundle block     — セットで購入. Shared (targets Appstle `.ab-bogo-*`).
//   3. Single block     — 単品で購入. Feature-detects the theme (Luxora custom
//                         vs Dawn) and adapts; see its header comment.
// ============================================================================

// ---------------------------------------------------------------------------
// 1. Shared preamble
// ---------------------------------------------------------------------------
// Localised UI strings. DOM-sourced text (titles, subtitles, prices) is
// already localised by Shopify — only our hardcoded literals need this.
const __bogoLocale = (window.Shopify?.locale || document.documentElement.lang || 'ja')
  .toLowerCase()
  .split('-')[0];
const T = {
  ja: { set: 'セットで購入', single: '単品で購入', badge: '限定特典', save: (n) => `¥${n}お得`, freeTrial: '初月無料でお試し', subscribe: '定期便', oneTime: '一回購入', bundleBuyButton: 'カートに追加する', singleBuyButton: 'カートに追加する' },
  en: { set: 'Buy as a set', single: 'Buy individually', badge: 'Limited offer', save: (n) => `Save ¥${n}`, freeTrial: 'First month free', subscribe: 'Subscribe', oneTime: 'One-time', bundleBuyButton: 'Add to cart', singleBuyButton: 'Add to cart' },
}[__bogoLocale === 'en' ? 'en' : 'ja'];

// Wrap every ¥ symbol in a span so it can be rendered smaller than the number
// beside it (CSS can't target a single character in a text node). Used wherever
// the widget writes a price/savings string; the values are widget/Appstle
// numbers, never user input, so innerHTML is safe here. Style: .bogo-yen.
const __bogoYen = (text) => String(text).replace(/¥/g, '<span class="bogo-yen">¥</span>');

// A DIRECT click on a purchase-option radio fires a bubbling `change`/`input`
// event. The host theme's variant-change logic reacts to any such event in the
// product area and re-renders the variant — which resets the Appstle selling
// plan and wipes the subscription discount (the price jumps from the discounted
// amount back to the regular one). Card-body/button clicks set `.checked`
// programmatically, which fires NO change event — that's why only a direct
// radio click triggered the bug. Stop these events at the radio so they never
// reach the theme's handler; the click itself still bubbles (selection works).
const __bogoIsolateRadio = (radio) => {
  radio.addEventListener('change', (e) => e.stopPropagation());
  radio.addEventListener('input', (e) => e.stopPropagation());
};

// ---------------------------------------------------------------------------
// Selected selling-plan description.
//
// Appstle renders the selected plan's description in an element with class
// `.appstleSelectedSellingPlanOptionDescription`, inside the standalone
// subscription widget (single product) — reflecting the SINGLE card's plan.
// The BOGO bundle widget renders NO such element, and each bundle row has its
// OWN plan selector, so mirroring that single element onto a bundle row would
// show the wrong plan's text. For the bundle we instead look the description up
// by the row's selected plan id in Appstle's embedded `selling_plans` JSON.
//
// Placement of the copy:
//   • single card  — BOTH themes surface Appstle's own standalone widget, which
//                    renders a live, correctly-positioned description below the
//                    plan dropdown; we just un-hide + style it via CSS (Dawn:
//                    under <product-info>; Luxora: under <product-page>), so no
//                    copy is registered here.
//   • bundle Buy X — a copy resolved from that row's own plan id (embedded map).
//   • bundle Get Y — Get Y is a DIFFERENT product, so its plan descriptions
//                    aren't embedded on this page; fetch them from Shopify's
//                    product .js (by the handle in the BOGO product link) and
//                    resolve against that map.
//
// Each place registers a { el, resolve } pair; one debounced document observer
// re-runs every resolver and writes/hides its node, so plan changes anywhere
// (variant switches that rewrite plans included) stay reflected.
const __bogoDescNodes = [];

// { sellingPlanId -> plaintext description } parsed from Appstle's embedded
// selling_plans JSON (present in a page <script>). Cached once populated.
let __bogoDescMapCache = null;
const __bogoDescMap = () => {
  if (__bogoDescMapCache && Object.keys(__bogoDescMapCache).length) return __bogoDescMapCache;
  const map = {};
  const strip = (html) => { const d = document.createElement('div'); d.innerHTML = html; return (d.textContent || '').trim(); };
  for (const sc of document.querySelectorAll('script')) {
    const t = sc.textContent || '';
    if (!t.includes('"selling_plans"')) continue;
    const re = /\{"id":(\d+),"name":"(?:[^"\\]|\\.)*","description":"((?:[^"\\]|\\.)*)"/g;
    let m;
    while ((m = re.exec(t))) {
      let desc = m[2];
      try { desc = JSON.parse('"' + desc + '"'); } catch (e) {}
      map[m[1]] = strip(desc);
    }
  }
  __bogoDescMapCache = map;
  return map;
};

// Plaintext description for a selling-plan <select>'s current value (BOGO rows).
const __bogoDescForSelect = (sel) => {
  if (!sel) return '';
  let id = null;
  try { id = String(JSON.parse(sel.value).id); } catch (e) {}
  return id ? (__bogoDescMap()[id] || '') : '';
};

// Shopify product handle from a product URL (…/products/<handle>[?#…]).
const __bogoHandleFromHref = (href) => {
  const m = String(href || '').match(/\/products\/([^/?#]+)/);
  return m ? m[1] : '';
};

// { handle -> Promise<{ sellingPlanId -> plaintext description }> } from
// Shopify's product .js, for products whose plan data isn't embedded on this
// page (the bundle's Get Y). Fetched once per handle (same-origin; the handle
// works even when the link points at the .myshopify domain) and cached; failures
// resolve to {} so the row simply shows no description.
const __bogoFetchedDescMaps = {};
const __bogoFetchDescMap = (handle) => {
  if (!handle) return Promise.resolve({});
  if (__bogoFetchedDescMaps[handle]) return __bogoFetchedDescMaps[handle];
  const strip = (html) => { const d = document.createElement('div'); d.innerHTML = html; return (d.textContent || '').trim(); };
  __bogoFetchedDescMaps[handle] = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => {
      const map = {};
      for (const g of (j && j.selling_plan_groups) || []) {
        for (const p of g.selling_plans || []) map[String(p.id)] = strip(p.description || '');
      }
      return map;
    })
    .catch(() => ({}));
  return __bogoFetchedDescMaps[handle];
};

const __bogoRefreshPlanDescs = () => {
  __bogoDescNodes.forEach(({ el, resolve }) => {
    const text = (resolve() || '').trim();
    if (text) {
      if (el.textContent !== text) el.textContent = text;
      if (el.style.display !== '') el.style.display = '';
    } else if (el.style.display !== 'none') {
      el.style.display = 'none';
    }
  });
};

let __bogoDescTimer = null;
// PERF: this observer does nothing until a description node is registered (nodes
// are only added when a card builds), yet as a whole-document characterData +
// subtree observer it was the single most expensive thing we ran — firing on
// every DOM mutation during Appstle/PageFly/GemPages' initial render storm and
// stealing main-thread time from Appstle's own rendering (which is exactly the
// ~1–2s the bundle takes to appear). Attach it LAZILY (only after the first card
// builds, when it finally has something to do) and scope it to the product area
// instead of the whole document. __bogoStartDescObserver() is idempotent.
let __bogoDescObserver = null;
const __bogoStartDescObserver = () => {
  if (__bogoDescObserver) return;
  __bogoDescObserver = new MutationObserver(() => {
    clearTimeout(__bogoDescTimer);
    __bogoDescTimer = setTimeout(__bogoRefreshPlanDescs, 100);
  });
  const root =
    document.querySelector('product-info, product-page') ||
    document.querySelector('main, #MainContent') ||
    document.documentElement;
  __bogoDescObserver.observe(root, { childList: true, subtree: true, characterData: true });
};

let __bogoDone = 0;

// ---------------------------------------------------------------------------
// Skeleton placeholder (replaces the old spinner).
//
// Appstle injects the real bundle markup client-side, which takes up to ~1s on
// a heavy page. The reveal gate (`html:not(.bogo-ready)` in the CSS) keeps the
// native controls + our real cards hidden until both are built, so without a
// placeholder the buy area would sit blank. Instead we drop in a skeleton shaped
// like the finished card: the static bits we already know — the 限定特典 badge and
// the セットで購入 / 単品で購入 labels + radios and the two-row bundle layout — are real,
// while the values only Appstle/Shopify can supply (prices, product names,
// images) are shimmer bars. __bogoReveal() removes it and fades the real card in.
//
// Anchored to the theme's own server-rendered buy area (present the moment this
// script runs), NOT the Appstle bundle (which doesn't exist yet), so it appears
// immediately. Mirrors buildCard()'s anchor logic.
// Force display + size INLINE with !important. Some app CSS on this theme hides
// empty <div>s (e.g. `div:empty { display:none }`), which zeroed every shimmer
// box (they render 0×0 from the class alone); inline !important beats it. The
// class still supplies the shimmer background/animation. `w`/`h` take a number
// (px) or a CSS string.
const __bogoSkelSize = (el, w, h) => {
  const v = (x) => (typeof x === 'number' ? x + 'px' : x);
  el.style.setProperty('display', 'block', 'important');
  if (w != null) el.style.setProperty('width', v(w), 'important');
  if (h != null) el.style.setProperty('height', v(h), 'important');
  return el;
};
const __bogoSkelBar = (cls, w, h) => {
  const b = document.createElement('div');
  b.className = 'bogo-skel-bar' + (cls ? ' ' + cls : '');
  return __bogoSkelSize(b, w, h);
};
// Selector-shaped shimmer (full width, capped like the real .ab-bogo-select).
const __bogoSkelSelect = () => {
  const s = __bogoSkelBar('bogo-skel-select', '100%', 38);
  s.style.setProperty('max-width', '320px', 'important');
  return s;
};
// Purchase-button placeholder: shows the real CTA text in a disabled/greyed
// style (not a shimmer) until the live button loads. It has text, so it isn't
// hit by the div:empty hide.
const __bogoSkelButton = (text) => {
  const b = document.createElement('div');
  b.className = 'bogo-skel-btn-cta';
  b.textContent = text;
  return b;
};

// Best-effort real data for the CURRENT product — i.e. the bundle's "Buy X"
// row and the page's own product. Every source is optional; if none resolve we
// keep the shimmer, so this can never break the skeleton. NO price is read here:
// prices stay placeholders until Appstle computes the (discounted) amount.
const __bogoCurrentProduct = () => {
  const clean = (s) => (s || '').replace(/\s+/g, ' ').trim();
  // Some themes render the product title twice in the DOM (a visually-hidden
  // screen-reader copy), so textContent comes back doubled ("Title Title") —
  // collapse an exact repeat back to a single copy.
  const dedupe = (s) => {
    const c = clean(s);
    const m = c.match(/^(.+?)\s*\1$/);
    return m ? m[1].trim() : c;
  };
  const meta =
    (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) || null;
  const title =
    dedupe(document.querySelector('.product__title, .product-title, .product-single__title, product-info h1')?.textContent) ||
    clean(document.querySelector('meta[property="og:title"]')?.getAttribute('content')) ||
    clean(meta && meta.title);
  const image =
    clean(document.querySelector('meta[property="og:image"]')?.getAttribute('content')) ||
    clean(document.querySelector('.product__media img, .product-media img, [id*="ProductMedia"] img')?.getAttribute('src'));
  return { title, image };
};

// Card header: radio + label on the left, a price placeholder on the right.
// Mirrors .bogo-header (+ .bogo-header-prices).
const __bogoSkelHeader = (label) => {
  const header = document.createElement('div');
  header.className = 'bogo-skel-header';
  const left = document.createElement('div');
  left.className = 'bogo-skel-header-left';
  const radio = document.createElement('span');
  radio.className = 'bogo-skel-radio';
  const lbl = document.createElement('span');
  lbl.className = 'bogo-skel-label';
  lbl.textContent = label;
  left.append(radio, lbl);
  const prices = document.createElement('div');
  prices.className = 'bogo-skel-header-prices';
  prices.appendChild(__bogoSkelBar('bogo-skel-price', 90, 22)); // price placeholder — no number until Appstle loads
  header.append(left, prices);
  return header;
};

// Product row mirroring a real .bogo-product: 150px image + content (title,
// price, one selector). Same height as the finished row, so nothing expands on
// load. `pos` ('first'/'last') drives the joined-border + "+" connector styling.
const __bogoSkelRow = (data, pos) => {
  const row = document.createElement('div');
  row.className = 'bogo-skel-product bogo-skel-product--' + pos;

  let imgEl;
  if (data && data.image) {
    imgEl = document.createElement('img');
    imgEl.className = 'bogo-skel-pimg bogo-skel-pimg-real';
    imgEl.src = data.image;
    imgEl.alt = '';
    imgEl.loading = 'eager';
    imgEl.addEventListener('error', () => imgEl.classList.remove('bogo-skel-pimg-real')); // shimmer if it 404s
  } else {
    imgEl = document.createElement('div');
    imgEl.className = 'bogo-skel-pimg';
    __bogoSkelSize(imgEl, 150, 150); // force through the div:empty hide
  }

  const content = document.createElement('div');
  content.className = 'bogo-skel-pcontent';

  const top = document.createElement('div');
  top.className = 'bogo-skel-ptop';
  if (data && data.title) {
    const t = document.createElement('div');
    t.className = 'bogo-skel-title-real';
    t.textContent = data.title;
    top.appendChild(t);
  } else {
    // Two-line shimmer where the (usually wrapping) product title will land, so
    // the text lands in the same place when the real card loads.
    const tl = document.createElement('div');
    tl.className = 'bogo-skel-title-lines';
    tl.append(
      __bogoSkelBar('bogo-skel-tline bogo-skel-tline--1', '90%', 13),
      __bogoSkelBar('bogo-skel-tline bogo-skel-tline--2', '55%', 13),
    );
    top.appendChild(tl);
  }
  top.appendChild(__bogoSkelBar('bogo-skel-pprice', 64, 20)); // price placeholder — no number until Appstle loads
  content.appendChild(top);

  content.appendChild(__bogoSkelSelect()); // variant/plan selector placeholder

  row.append(imgEl, content);
  return row;
};

// Bundle card (セットで購入): badge + header + two product rows + button — mirrors
// .bogo-custom-container so the skeleton is the same shape/height as the result.
const __bogoSkelBundle = (hero) => {
  const card = document.createElement('div');
  card.className = 'bogo-skel-card bogo-skel-bundle';
  const badge = document.createElement('span');
  badge.className = 'bogo-skel-badge';
  badge.textContent = T.badge;
  card.appendChild(badge);
  card.appendChild(__bogoSkelHeader(T.set));
  card.appendChild(__bogoSkelRow(hero, 'first')); // Buy X — real name/image
  card.appendChild(__bogoSkelRow(null, 'last'));  // Get Y — shimmer (needs Appstle)
  card.appendChild(__bogoSkelButton(T.bundleBuyButton));
  return card;
};

// Single card (単品で購入): header + selector + button — mirrors #lf-card.
const __bogoSkelSingle = () => {
  const card = document.createElement('div');
  card.className = 'bogo-skel-card bogo-skel-single';
  card.appendChild(__bogoSkelHeader(T.single));
  card.appendChild(__bogoSkelSelect());
  card.appendChild(__bogoSkelButton(T.singleBuyButton));
  return card;
};

const __bogoHero = __bogoCurrentProduct(); // real name/image for the Buy X row
const __bogoSkeleton = document.createElement('div');
__bogoSkeleton.id = 'bogo-skeleton';
__bogoSkeleton.setAttribute('aria-hidden', 'true');
__bogoSkeleton.appendChild(__bogoSkelBundle(__bogoHero));
// Only add the single-purchase placeholder when the page actually has a single
// buy area (mirrors the real single block's own bail-out), so bundle-only pages
// don't reserve height for a card that never appears.
if (document.querySelector('product-form, .product-price, .elife-price-wrapper')) {
  __bogoSkeleton.appendChild(__bogoSkelSingle());
}

const __bogoPlaceSkeleton = () => {
  if (__bogoSkeleton.parentNode) return true;
  const anchor =
    document.querySelector('.product-variant__container') ||
    document.querySelector('.product-variant') ||
    document.querySelector('product-form') ||
    document.querySelector('.product-price, .elife-price-wrapper');
  if (anchor && anchor.parentNode) {
    anchor.parentNode.insertBefore(__bogoSkeleton, anchor);
    return true;
  }
  const root = document.querySelector('product-page, product-info');
  if (root) { root.prepend(__bogoSkeleton); return true; }
  return false;
};

// Place it now; if the buy area isn't parsed yet, watch briefly for it.
if (!__bogoPlaceSkeleton()) {
  const __skelObs = new MutationObserver(() => {
    if (__bogoPlaceSkeleton()) __skelObs.disconnect();
  });
  __skelObs.observe(document.documentElement, { childList: true, subtree: true });
  setTimeout(() => __skelObs.disconnect(), 15000);
}

const __bogoReveal = () => {
  const p = document.querySelector('.ab-bogo-discount-product-page');
  if (p) p.style.display = 'block';
  // Remove the skeleton from flow first (so it doesn't stack under the real card
  // during the fade), then drop the gate — the CSS fades the real card in.
  __bogoSkeleton.remove();
  document.documentElement.classList.add('bogo-ready');
  console.log('[bogo] revealed (skeleton removed, real card faded in)');
};

window.__bogoReady = () => {
  __bogoDone++;
  console.log('[bogo] __bogoReady fired, count =', __bogoDone, '/ 2');
  if (__bogoDone >= 2) {
    console.log('[bogo] both blocks ready → reveal');
    __bogoReveal();
  }
};

// ---------------------------------------------------------------------------
// 2. Bundle block — セットで購入 (UNCHANGED from the original store)
// ---------------------------------------------------------------------------
(function () {
  const HARD_TIMEOUT_MS = 15000;

  const SELECTORS = {
    bundleContent: '.ab-bogo-bundle-content',
    productContainer: '.ab-bogo-product-container',
    img: 'img',
    productLink: '.ab-bogo-product-link',
    originalPrice: '.ab-bogo-product-original-price',
    discountedPrice: '.ab-bogo-product-discounted-price, .ab-bogo-product-price',
    variantSelect: '.ab-bogo-variant-select',
    sellingPlanSelect: '.ab-bogo-selling-plan-select',
    button: 'button',
  };

  const CLASSES = {
    customContainer: 'bogo-custom-container',
    header: 'bogo-header',
    headerLeft: 'bogo-header-left',
    headerPrices: 'bogo-header-prices',
    product: 'bogo-product',
    productImg: 'bogo-product-img',
    productContent: 'bogo-product-content',
    productTop: 'bogo-product-top',
    productTitle: 'bogo-product-title',
    productPrices: 'bogo-product-prices',
    containerBadge: 'bogo-container-badge',
    bundleSavings: 'bogo-bundle-savings',
  };

  // ---------------------------------------------------------------------------
  // Clone ⇄ native sync.
  //
  // We show the shopper a restyled COPY of Appstle's <select>s (see cloneSelect)
  // while Appstle keeps managing the hidden ORIGINALS — it rebuilds them in
  // place on every variant change (rewriting the plan options, e.g. Variant B
  // collapses to a single plan). A clone is a one-time snapshot, so without
  // this it drifts: offering plans the current variant no longer has (which
  // then can't be selected). syncCloneOptions re-mirrors the live native state
  // onto the clone; the priceObserver calls it on every bundle mutation.
  //
  // We do NOT copy Appstle's `disabled` attribute: it toggles transiently while
  // Appstle rebuilds and stays stuck `true` after a programmatic variant change,
  // which would freeze the clone disabled even with multiple plans available.
  // Instead we derive it: one option = nothing to choose = disabled.
  // ---------------------------------------------------------------------------
  const syncPairs = [];
  const sigOf = (s) => Array.from(s.options).map((o) => `${o.value}\x00${o.text}`).join('|');
  const syncCloneOptions = (original, clone) => {
    if (sigOf(original) !== sigOf(clone)) {
      clone.textContent = '';
      for (const o of original.options) {
        const opt = document.createElement('option');
        opt.value = o.value;
        opt.textContent = o.text;
        clone.appendChild(opt);
      }
    }
    const shouldDisable = clone.options.length <= 1;
    if (clone.disabled !== shouldDisable) clone.disabled = shouldDisable;
    if (clone.value !== original.value) clone.value = original.value;
  };

  const parsePriceText = (text) => {
    const s = String(text);
    const digits = s.replace(/[^\d.]/g, '');
    // No numeric amount: treat an explicit "free" label (無料 / Free) as ¥0, so a
    // free Get-Y gift settles instead of failing the row sanity check as NaN.
    if (digits === '') return /無料|free/i.test(s) ? 0 : NaN;
    const n = parseFloat(digits);
    return isNaN(n) ? NaN : n;
  };

  // Hide a crossed-out (compare-at) price when it equals the current price —
  // a strike-through of the same amount is just noise. Re-run on every refresh.
  const toggleCompareAt = (el, orig, cur) => {
    if (!el) return;
    const same = orig == null || parsePriceText(orig) === parsePriceText(cur);
    const display = same ? 'none' : '';
    if (el.style.display !== display) el.style.display = display;
  };

  // On the "Get Y" row, show the "初月無料でお試し" badge (above the title) when
  // its price is 0 (free); the ¥0 price stays visible. Idempotent + re-run on
  // every price refresh so it toggles as the shopper changes plans/variants.
  // No-ops on rows without a badge (i.e. the Buy X row).
    const applyFreeBadge = (row, priceStr) => {
      if (!row) return;
      const badge = row.querySelector('.bogo-free-badge');
      if (!badge) return; // no badge → Buy X row, nothing to toggle
      const free = parsePriceText(priceStr) === 0;
      badge.style.display = free ? '' : 'none';
    
      // Disable the Y title link while the product is free (price 0). Stash the
      // href so it can be restored, and block pointer/keyboard activation.
      const title = row.querySelector('a.bogo-product-title');
      if (title) {
        if (free) {
          if (title.hasAttribute('href')) {
            title.dataset.href = title.getAttribute('href');
            title.removeAttribute('href');
          }
          title.style.pointerEvents = 'none';
          title.setAttribute('aria-disabled', 'true');
        } else {
          if (!title.hasAttribute('href') && title.dataset.href) title.setAttribute('href', title.dataset.href);
          title.style.pointerEvents = '';
          title.removeAttribute('aria-disabled');
        }
      }
    };

  const readPrice = (el) => {
    if (!el) return '';
    const disc = el.querySelector('.ab-bogo-product-discounted-price');
    return (disc || el).textContent.trim();
  };

  const extract = (c) => {
    const img = c.querySelector(SELECTORS.img);
    const orig = c.querySelector(SELECTORS.originalPrice);
    const link = c.querySelector(SELECTORS.productLink);
    const priceEl = c.querySelector(SELECTORS.discountedPrice);
    const variant = c.querySelector(SELECTORS.variantSelect);
    const sellingPlan = c.querySelector(SELECTORS.sellingPlanSelect);

    return {
      imgSrc: img ? img.src : '',
      productTitle: link?.textContent.trim() || '',
      productHref: link ? link.getAttribute('href') : '',
      originalPrice: orig ? orig.textContent.trim() : null,
      price: readPrice(priceEl),
      variantSelect: variant || null,
      sellingPlan: sellingPlan || null,
    };
  };

  const sumPrices = (products, pick) =>
    products.reduce((sum, p) => {
      const value = parsePriceText(pick(p));
      return isNaN(value) ? sum : sum + value;
    }, 0);

  const computeWas = (products) => sumPrices(products, (p) => p.originalPrice ?? p.price);
  const computeNow = (products) => sumPrices(products, (p) => p.price);

  // A row's price text existing isn't enough — early in the render it can
  // still hold the pre-discount (regular) price. But we must NOT require
  // every row to be discounted: this is a "buy X, get Y" bundle, where
  // product X is intentionally sold at full price (cur == orig) and only
  // product Y is discounted. So the per-row check is a sanity check only
  // (a current price can't legitimately exceed its own original). The real
  // "discount has landed" signal is the bundle-level `was > now` check: if
  // Y still shows its pre-discount price, the row totals are equal and we
  // keep waiting; once Y drops, the total drops and we settle. Both totals
  // are summed from the rows themselves rather than read from any
  // bundle-level aggregate element (avoiding the separate scripts/races
  // that compute those aggregates independently).
  const bundleLooksSettled = (containers) => {
    const products = [...containers].map(extract);
    if (products.some((p) => !p.price)) {
      console.log('[bogo] not settled: a row has no price', products.map((p) => p.price));
      return null;
    }

    const rowsSane = products.every((p) => {
      if (p.originalPrice == null) return true;
      const orig = parsePriceText(p.originalPrice);
      const cur = parsePriceText(p.price);
      return !isNaN(orig) && !isNaN(cur) && cur <= orig;
    });
    if (!rowsSane) {
      console.log('[bogo] not settled: a row is insane (cur > orig)', products.map((p) => ({ o: p.originalPrice, c: p.price })));
      return null;
    }

    const now = computeNow(products);
    const was = computeWas(products);
    if (was <= now) {
      console.log('[bogo] not settled: discount not landed, was=', was, 'now=', now);
      return null;
    }

    console.log('[bogo] settled ✓ was=', was, 'now=', now);
    return { products, now, was };
  };

  const buildHeader = (now, was) => {
    const header = document.createElement('div');
    header.className = CLASSES.header;

    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = 'bogo-purchase-option';
    radio.className = 'bogo-radio';
    radio.value = 'bundle';
    radio.checked = true;
    // Prevent the theme's <product-variants> getSelectedOptionHash() from
    // crashing on our radio (it does el.selectedOptions[0]?.… on every
    // checked input). An empty selectedOptions makes that yield undefined,
    // which the theme filters out — so, unlike a data-option-value-id, it
    // does NOT pollute the hash. That hash is the theme's variant-prefetch
    // CACHE KEY: polluting it turns every switch into a cold ~1s
    // Section-Rendering fetch instead of an instant cache hit.
    radio.selectedOptions = [];
    __bogoIsolateRadio(radio);

    const heading = document.createElement('span');
    heading.textContent = T.set;

    let savings = null;
    if (was > now) {
      savings = document.createElement('span');
      savings.className = CLASSES.bundleSavings;
      savings.innerHTML = __bogoYen(T.save(Math.round(was - now).toLocaleString('ja-JP')));
    }

    const makeDiv = (text, cls) => {
      const d = document.createElement('div');
      d.className = cls;
      d.innerHTML = __bogoYen(text);
      return d;
    };
    const orig = makeDiv('¥' + was.toLocaleString('ja-JP'), 'custom-compare-at-price');
    const price = makeDiv('¥' + now.toLocaleString('ja-JP'), 'custom-price');
    toggleCompareAt(orig, was, now);

    // Bundle title pulled from the existing DOM (`.ab-bogo-title`), shown
    // as a full-width row BELOW both セットで購入 and the prices.
    const titleText = document.querySelector('.ab-bogo-title')?.textContent.trim();
    let title = null;
    if (titleText) {
      title = document.createElement('div');
      title.className = 'bogo-bundle-title';
      title.textContent = titleText;
    }

    // Radio on the same line as セットで購入 (+savings).
    const headingRow = document.createElement('div');
    headingRow.style.display = 'flex';
    headingRow.style.alignItems = 'center';
    headingRow.style.gap = '8px';
    radio.style.margin = '0';
    [radio, heading, savings].forEach((el) => el && headingRow.appendChild(el));

    const left = document.createElement('div');
    left.className = CLASSES.headerLeft;
    left.appendChild(headingRow);

    const prices = document.createElement('div');
    prices.className = CLASSES.headerPrices;
    [orig, price].forEach((el) => el && prices.appendChild(el));

    // Top row: heading (left) + prices (right); title goes beneath it.
    const topRow = document.createElement('div');
    topRow.className = 'bogo-header-top';
    topRow.appendChild(left);
    topRow.appendChild(prices);

    header.appendChild(topRow);
    if (title) header.appendChild(title);
    return header;
  };

  const cloneSelect = (original) => {
    const clone = original.cloneNode(true);
    clone.removeAttribute('id');
    clone.removeAttribute('name');
    // The native selling-plan <select> ships with an inline `display:none`
    // that Appstle only overrides via a rule scoped to its own
    // `.ab-bogo-select-content` wrapper. cloneNode copies that inline style,
    // but the clone lands in `.bogo-product-content` — outside that rule's
    // scope — so it would stay hidden. Clear it so the clone renders in flow.
    clone.style.display = '';
    clone.selectedIndex = original.selectedIndex;
    clone.addEventListener('change', () => {
      original.value = clone.value;
      original.dispatchEvent(new Event('change', { bubbles: true }));
    });
    return clone;
  };

    const buildProduct = (data, isGet) => {
      const div = document.createElement('div');
      div.className = CLASSES.product;
  
      if (data.imgSrc) {
        const img = document.createElement('img');
        img.className = CLASSES.productImg;
        img.src = data.imgSrc;
        div.appendChild(img);
      }
  
      const content = document.createElement('div');
      content.className = CLASSES.productContent;
  
      const top = document.createElement('div');
      top.className = CLASSES.productTop;
  
      // Only the "Get Y" row's title links to its own product page (using the
      // Appstle product link). The Buy X row stays a plain <div>. When Y is free
      // (price 0) the link is disabled — applyFreeBadge() toggles that below, so
      // it stays in sync as the shopper changes plans/variants.
      const isLinkable = isGet && !!data.productHref;
      const title = document.createElement(isLinkable ? 'a' : 'div');
      title.className = CLASSES.productTitle;
      title.textContent = data.productTitle;
      if (isLinkable) title.href = data.productHref;
      top.appendChild(title);
  
      const prices = document.createElement('div');
      prices.className = CLASSES.productPrices;
  
      const p = document.createElement('div');
      p.className = `custom-sub-price`;
      p.innerHTML = __bogoYen(data.price);
      prices.appendChild(p);
  
      // Always created (hidden when absent / same as the price) so
      // refreshPrices() can show it once a real discount appears.
      const o = document.createElement('div');
      o.className = `custom-sub-compare-at-price`;
      o.innerHTML = __bogoYen(data.originalPrice ?? '');
      toggleCompareAt(o, data.originalPrice, data.price);
      prices.appendChild(o);
  
      top.appendChild(prices);
  
      // "Get Y" only: a free-trial badge shown ABOVE the product title when the
      // Get Y price is 0 (the ¥0 price stays visible). Hidden by default;
      // applyFreeBadge() below toggles it.
      if (isGet) {
        const badge = document.createElement('span');
        badge.className = 'bogo-free-badge';
        badge.textContent = T.freeTrial;
        badge.style.display = 'none';
        content.appendChild(badge); // first child of content → above the title row
      }
      content.appendChild(top);
  
      // Render a select: single-option → static text, otherwise a synced
      // clone. Variant selector (e.g. 2.5/5/7.5mg) first, then the selling
      // plan — matching the native bundle order.
      const renderSelect = (sel) => {
        if (!sel) return;
        if (sel.options.length <= 1) {
          const txt = document.createElement('div');
          txt.className = 'custom-selling-plan-text';
          txt.textContent = sel.options[0]?.text || '';
          content.appendChild(txt);
        } else {
          const clone = cloneSelect(sel);
          syncPairs.push({ original: sel, clone }); // keep it synced to the native select
          content.appendChild(clone);
        }
      };
      renderSelect(data.variantSelect);
      renderSelect(data.sellingPlan);
  
      // Selected selling-plan description, resolved from THIS row's own selected
      // plan id so it always matches the plan shown in the row's selector.
      //   • Buy X — the page's main product; its plan descriptions are embedded in
      //             the page (__bogoDescForSelect / __bogoDescMap).
      //   • Get Y — a DIFFERENT product, so its plan data isn't on this page;
      //             fetch it from Shopify's product .js (by the handle in the BOGO
      //             product link) and resolve against that map.
      // Both stay hidden until the selected plan actually has a description.
      {
        const desc = document.createElement('div');
        desc.className = 'bogo-plan-description';
        desc.style.display = 'none';
        content.appendChild(desc);
  
        const planId = () => {
          if (!data.sellingPlan) return null;
          try { return String(JSON.parse(data.sellingPlan.value).id); } catch (e) { return null; }
        };
  
        if (!isGet) {
          __bogoDescNodes.push({ el: desc, resolve: () => __bogoDescForSelect(data.sellingPlan) });
        } else {
          let getYMap = null;
          __bogoFetchDescMap(__bogoHandleFromHref(data.productHref)).then((m) => {
            getYMap = m;
            __bogoRefreshPlanDescs(); // populate once the fetch lands
          });
          __bogoDescNodes.push({
            el: desc,
            resolve: () => {
              const id = planId();
              return getYMap && id ? (getYMap[id] || '') : '';
            },
          });
        }
      }
  
      div.appendChild(content);
      applyFreeBadge(div, data.price); // initial free-trial-badge state (Get Y)
      return div;
    };

  // Returns true once built (or already built), false if not ready yet.
  // No setTimeout wrapping here — the return value is real and synchronous,
  // so the MutationObserver retry loop below actually works.
  const tryBuild = () => {
    const bundle = document.querySelector(SELECTORS.bundleContent);
    if (!bundle) {
      console.log('[bogo] tryBuild: no', SELECTORS.bundleContent, 'in DOM yet');
      return false;
    }

    if (bundle.dataset.bogoProcessed) return true;

    const containers = document.querySelectorAll(SELECTORS.productContainer);
    if (containers.length < 2) {
      console.log('[bogo] tryBuild: only', containers.length, 'product container(s), need ≥2');
      return false;
    }

    const settled = bundleLooksSettled(containers);
    if (!settled) {
      return false;
    }

    console.log('[bogo] tryBuild: building custom container');
    bundle.dataset.bogoProcessed = '1';
    bundle.style.display = 'none';

    const [buyXProduct, getYProduct] = settled.products;

    const parent = document.createElement('div');
    parent.className = CLASSES.customContainer;

    const badge = document.createElement('span');
    badge.className = CLASSES.containerBadge;
    badge.textContent = T.badge;
    parent.appendChild(badge);

    parent.appendChild(buildHeader(settled.now, settled.was));
    // `html.bogo-multi` (set in theme.liquid for product.test-shop-forced-bundle
    // only): render EVERY bundle product, not just the first two. Rows after
    // the first are treated like Get Y (own product link, fetched plan descs,
    // free-trial badge when ¥0).
    if (document.documentElement.classList.contains('bogo-multi')) {
      settled.products.forEach((prod, i) => parent.appendChild(buildProduct(prod, i > 0)));
    } else {
      parent.appendChild(buildProduct(buyXProduct));
      parent.appendChild(buildProduct(getYProduct, true)); // Get Y → free-trial badge enabled
    }

  const enforceButtonText = (button, text) => {
  const apply = () => {
    const loading = button.disabled; // during load the theme disables + shows only the spinner

    let span = button.querySelector('.bogo-btn-label');
    if (!span) {
      if (loading) return; // spinner is showing; don't inject our label
      // strip bare text nodes only; never remove the spinner <svg>
      [...button.childNodes].forEach((n) => { if (n.nodeType === Node.TEXT_NODE) n.remove(); });
      span = document.createElement('span');
      span.className = 'bogo-btn-label';
      button.appendChild(span);
    }
    span.style.display = loading ? 'none' : '';
    if (!loading && span.textContent !== text) span.textContent = text;
  };
  apply();
  const mo = new MutationObserver(apply);
  mo.observe(button, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
  return mo;
  };

    const button =
      bundle.querySelector(`${SELECTORS.productContainer} ${SELECTORS.button}`) ||
      bundle.querySelector(SELECTORS.button);
    if (button) {
      parent.appendChild(button);
      enforceButtonText(button, T.bundleBuyButton);
    } else {
      console.warn('[bogo] tryBuild: no buy button found to move into custom container');
    }

    bundle.parentNode.insertBefore(parent, bundle);

    // Clicking anywhere inside the bundle container (incl. the buy button)
    // selects its radio.
    parent.addEventListener('click', () => {
      parent.querySelector('.bogo-radio').checked = true;
    });

    // Keep the custom container in sync with the native bundle. On any native
    // mutation we (1) re-mirror each cloned <select>'s options from its native
    // original (so a variant switch that rewrites the plans lands on the
    // clones), then (2) re-read and mirror the recomputed prices into the
    // header + rows. We observe the native bundle CONTAINER (stable) so we
    // still catch changes even when the app replaces price nodes inside it.
    // Both steps are idempotent (write only on real changes), so mirroring
    // into `parent` — and the clones' own change handlers — can't loop.
    const refreshPrices = () => {
      const s = bundleLooksSettled(document.querySelectorAll(SELECTORS.productContainer));
      if (!s) return;

      const setText = (el, val) => { if (el && el.textContent !== val) el.innerHTML = __bogoYen(val); };

      setText(parent.querySelector(`.${CLASSES.header} .custom-price`), '¥' + s.now.toLocaleString('ja-JP'));
      const headerOrigEl = parent.querySelector(`.${CLASSES.header} .custom-compare-at-price`);
      setText(headerOrigEl, '¥' + s.was.toLocaleString('ja-JP'));
      toggleCompareAt(headerOrigEl, s.was, s.now);

      const savingsEl = parent.querySelector(`.${CLASSES.bundleSavings}`);
      if (savingsEl) {
        if (s.was > s.now) {
          setText(savingsEl, T.save(Math.round(s.was - s.now).toLocaleString('ja-JP')));
          savingsEl.style.display = '';
        } else {
          savingsEl.style.display = 'none';
        }
      }

      const rows = parent.querySelectorAll(`.${CLASSES.product}`);
      s.products.forEach((prod, i) => {
        const row = rows[i];
        if (!row) return;
        setText(row.querySelector('.custom-sub-price'), prod.price);
        const origEl = row.querySelector('.custom-sub-compare-at-price');
        if (origEl && prod.originalPrice) setText(origEl, prod.originalPrice);
        toggleCompareAt(origEl, prod.originalPrice, prod.price);
        applyFreeBadge(row, prod.price); // toggle the Get Y free-trial badge
      });
    };

    let refreshTimer = null;
    const priceObserver = new MutationObserver(() => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        syncPairs.forEach(({ original, clone }) => syncCloneOptions(original, clone));
        refreshPrices();
      }, 100);
    });
    priceObserver.observe(bundle, { childList: true, subtree: true, characterData: true });

    __bogoRefreshPlanDescs();
    __bogoStartDescObserver(); // PERF: attach the plan-desc observer only now (post-build), scoped
    console.log('[bogo] tryBuild: built ✓');
    window.__bogoReady();
    return true;
  };

  if (tryBuild()) return;

  // Debounced: a burst of mutations (e.g. a price element's text swapping
  // from regular to discounted) can leave things mid-update on any single
  // mutation record. Wait for a short quiet period before each attempt
  // instead of checking on every single DOM change.
  const DEBOUNCE_MS = 250;
  let debounceTimer = null;
  const observer = new MutationObserver(() => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      if (tryBuild()) {
        observer.disconnect();
        clearTimeout(hardTimeout);
      }
    }, DEBOUNCE_MS);
  });
  // PERF: scope the "wait for Appstle to inject the bundle" observer to the
  // product area instead of the whole document, so it doesn't fire on every
  // unrelated PageFly/GemPages mutation while Appstle is still rendering.
  observer.observe(
    document.querySelector('product-info, product-page') ||
      document.querySelector('main, #MainContent') ||
      document.documentElement,
    { childList: true, subtree: true },
  );

  // Bail out rather than spin forever if something never settles. On a
  // template with no bundle this fires — count the block as "done, nothing to
  // show" so the reveal isn't blocked waiting for a block that will never build.
  const hardTimeout = setTimeout(() => {
    console.warn('[bogo] HARD TIMEOUT after', HARD_TIMEOUT_MS, 'ms — no bundle on this template, marking block done');
    observer.disconnect();
    window.__bogoReady();
  }, HARD_TIMEOUT_MS);
})();

// ---------------------------------------------------------------------------
// 3. Single block — 単品で購入 (UNIFIED: works on BOTH the Luxora custom theme
//    and the elife Dawn-family theme)
// ---------------------------------------------------------------------------
// The two themes expose the single-product buy area very differently. Rather
// than two files, this block feature-detects and adapts:
//
//   role                Luxora (custom)          elife (Dawn)
//   ------------------  -----------------------  -------------------------
//   price source        .product-price           .elife-price-wrapper
//   variant/plan picker  <product-variant> selects (moved into the card)
//                                                (none — Appstle widget instead)
//   card body / actions  .product-variant panels  <product-form> (holds the
//                        + .product-actions        Appstle selector + button)
//   plain add-to-cart    .add-to-cart (hidden)    .product-form__submit (hidden)
//   page root            <product-page>           <product-info>
//   subtitle             .product__subtitle       (none)
//
// "Dawn" is detected by the presence of <product-info> (Luxora uses
// <product-page>). Selectors that only exist on one theme resolve to null on
// the other, so the shared code paths stay inert where they don't apply.
(function () {
  let attempt = 0;

  const parseYen = (s) => {
    const n = parseFloat(String(s).replace(/[^\d.]/g, ''));
    return isNaN(n) ? NaN : n;
  };

  // Read just the ¥ amounts from the current price block. Returns
  // { current, struck, savings }. Re-queried fresh each call so it reflects
  // the latest selling-plan selection (Appstle rewrites these nodes on change).
  const readPrices = () => {
    // "Buy without subscription" (one-time) selected: Appstle does NOT update the
    // main price block for the one-time option, so read its price straight from
    // the widget and show it with no strike/savings (it's the full price).
    const checkedPlan = document.querySelector('#appstle_subscription_widget0 input[name="selling_plan"]:checked');
    if (checkedPlan && checkedPlan.value === '') {
      const otEl = document.querySelector('#appstle_subscription_widget0 .appstle_one_time_price_wrapper .appstle_subscription_amount');
      const cur = otEl && (otEl.textContent.match(/¥[\d,]+/) || [])[0];
      if (cur) return { current: cur, struck: null, savings: null };
    }

    // Luxora: .product-price   |   elife (Dawn): .elife-price-wrapper
    const priceSrc = document.querySelector('.product-price, .elife-price-wrapper');
    if (!priceSrc) return null;

    const priceFrom = (el) => {
      if (!el) return null;
      const clone = el.cloneNode(true);
      clone.querySelectorAll('.visually-hidden').forEach((n) => n.remove());
      const m = clone.textContent.match(/¥[\d,]+/);
      return m ? m[0] : null;
    };

    // Appstle's currently-selected-plan price (injected on both themes).
    const finalEl = priceSrc.querySelector('.appstle_subscription_final_price');
    // The struck ("was") price. Appstle stamps its block
    // `data-appstle-price-modified="true"`; on Dawn that block wraps both the
    // regular (struck) and the sale amount, and priceFrom() takes the first ¥
    // amount (the regular). Fall back to each theme's own compare element.
    const compareEl =
      priceSrc.querySelector('[data-appstle-price-modified="true"] .price-item--regular') ||
      priceSrc.querySelector('[data-appstle-price-modified="true"]') ||
      priceSrc.querySelector('.price-item--regular') ||          // Dawn
      priceSrc.querySelector('.product-price--compare') ||       // Luxora
      priceSrc.querySelector('.price__compare');
    let current = priceFrom(finalEl);
    let struck = priceFrom(compareEl);

    if (!current) {
      const amounts = [...new Set(priceSrc.textContent.match(/¥[\d,]+/g) || [])]
        .map((label) => ({ label, value: parseFloat(label.replace(/[^\d.]/g, '')) }))
        .filter((a) => !isNaN(a.value))
        .sort((a, b) => a.value - b.value);
      current = amounts[0]?.label || priceSrc.textContent.trim();
      struck = amounts.length >= 2 ? amounts[amounts.length - 1].label : null;
    }

    let savings = null;
    const w = parseYen(struck);
    const n = parseYen(current);
    if (!isNaN(w) && !isNaN(n) && w > n) savings = w - n;

    return { current, struck, savings };
  };

  function buildCard() {
    attempt++;

    if (document.getElementById('lf-card')) {
      return true;
    }

    // Theme detection. Luxora uses <product-page>; Dawn (elife) uses
    // <product-info> and has no <product-page>. Requiring the ABSENCE of
    // <product-page> means that even if some theme carried both custom
    // elements, Luxora is never misdetected as Dawn.
    const isDawn = !document.querySelector('product-page') && !!document.querySelector('product-info');

    // Native theme variant/selling-plan picker. Luxora renders one and we move
    // it into the card. Dawn has none here (the Appstle widget is the selector),
    // so this resolves to null and the move is skipped.
    const variant =
      document.querySelector('.product-variant__container') ||
      document.querySelector('.product-variant') ||
      null;

    // The price block is both the readiness signal and the value we mirror.
    const priceSrc = document.querySelector('.product-price, .elife-price-wrapper');
    if (!priceSrc) {
      console.log('[single] buildCard attempt', attempt, ': no price block yet');
      return false;
    }

    // Wait only while Appstle is genuinely still injecting (Luxora stamps
    // `_processed` on this selector). Absent on Dawn → guard skipped.
    const appstleEl = priceSrc.querySelector('.appstle_stand_alone_price_display_selector');
    if (appstleEl && !appstleEl.classList.contains('appstle_stand_alone_price_display_selector_processed')) {
      console.log('[single] buildCard attempt', attempt, ': Appstle price selector not _processed yet');
      return false;
    }

    const finalEl = priceSrc.querySelector('.appstle_subscription_final_price');
    const wasEl = priceSrc.querySelector('[data-appstle-price-modified="true"]');

    // A discounted plan injects a final price AND a modified "was" together; a
    // final price with no "was" yet means we caught it mid-injection. The
    // reverse (a "was" with no final) is a valid terminal state, so don't block.
    if (finalEl && !wasEl) {
      console.log('[single] buildCard attempt', attempt, ': mid-injection (final price, no "was" yet)');
      return false;
    }

    console.log('[single] buildCard attempt', attempt, ': building card (', isDawn ? 'Dawn' : 'Luxora', ')');
    const card = document.createElement('div');
    card.id = 'lf-card';
    card.setAttribute('data-morph-skip', '');

    // Insert above the buy area: before the native picker (Luxora), else before
    // <product-form> (Dawn only — Luxora also has a <product-form> but keeps it
    // neutralised and anchors to the price block instead), else before the price
    // block, else at page top.
    const form = document.querySelector('product-form');
    const anchor = variant || (isDawn && form) || priceSrc;
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(card, anchor);
    } else {
      (document.querySelector('product-page, product-info') || document.body).prepend(card);
    }

    const header = document.createElement('div');
    header.className = 'single-product-header';

    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = 'bogo-purchase-option';
    radio.className = 'bogo-radio';
    radio.value = 'single';
    // Empty selectedOptions keeps the theme's option-hash scan (Luxora
    // <product-variants>) from crashing on this radio without polluting the
    // hash. Harmless on Dawn (no such scan).
    radio.selectedOptions = [];
    __bogoIsolateRadio(radio);

    const label = document.createElement('div');
    label.textContent = T.single;

    const savingsEl = document.createElement('span');
    savingsEl.className = 'bogo-bundle-savings';
    savingsEl.style.display = 'none';

    const labelRow = document.createElement('div');
    labelRow.style.display = 'flex';
    labelRow.style.alignItems = 'center';
    labelRow.style.gap = '8px';
    radio.style.margin = '0';
    [radio, label, savingsEl].forEach((el) => labelRow.appendChild(el));

    const textCol = document.createElement('div');
    [labelRow].forEach((el) => el && textCol.appendChild(el));

    const heading = document.createElement('div');
    heading.className = 'bogo-header-left';
    heading.appendChild(textCol);

    header.appendChild(heading);

    // Price group: struck price before current (matching .bogo-header-prices).
    const priceGroup = document.createElement('div');
    priceGroup.className = 'bogo-header-prices';

    const wasCopy = document.createElement('div');
    wasCopy.className = 'custom-compare-at-price';

    const priceCopy = document.createElement('div');
    priceCopy.className = 'custom-price';

    priceGroup.appendChild(wasCopy);
    priceGroup.appendChild(priceCopy);
    header.appendChild(priceGroup);

    // DAWN ONLY: surface ONLY the Appstle plan dropdown (.appstle_select) in the
    // card body; hide the rest of Appstle's purchase-options chrome (widget
    // title, Subscribe & Save radio + SAVE badge + price, DELIVERY FREQUENCY
    // label, Subscriber Rewards table). Appstle guards several of these with
    // `#appstle_subscription_widget… .x { … !important }` (ID specificity),
    // which a class-based stylesheet rule CANNOT beat — so we set the hides as
    // inline `!important` (inline important wins over any external rule) and
    // re-apply on every widget mutation (see the observer below). We do NOT
    // move the <select>: Appstle keeps managing it in place, so leaving it put
    // preserves its change handler. Gated on isDawn so it never touches Luxora
    // (whose selector is the native <product-variant> picker moved below).
    const APPSTLE_CHROME = [
      '.appstle_widget_title',
      '.appstle_subscription_radio_wrapper',
      '.appstle_select_label',
      '.appstleLoyaltyTable',
      '.appstle_bundle_discount_amount',
      '.appstle_subscription_description_text',
      '.appstle_fields_wrapper',
      '.appstle-tootltip-parent',
    ];
    const trimAppstleWidget = () => {
      if (!isDawn) return;
      const widget = document.querySelector('.appstle_sub_widget');
      if (!widget) return;
      APPSTLE_CHROME.forEach((sel) =>
        widget.querySelectorAll(sel).forEach((el) => el.style.setProperty('display', 'none', 'important')),
      );
      // Flatten the boxed "selected" background so only the dropdown remains.
      widget.querySelectorAll('.appstle_subscription_wrapper_option').forEach((el) => {
        el.style.setProperty('padding', '0', 'important');
        el.style.setProperty('border', 'none', 'important');
        el.style.setProperty('background', 'none', 'important');
        el.style.setProperty('box-shadow', 'none', 'important');
      });
      // Appstle ID-overrides the dropdown's border-radius; pin it to the card's.
      widget.querySelectorAll('.appstle_select').forEach((el) => el.style.setProperty('border-radius', '8px', 'important'));
    };
    trimAppstleWidget();

    // DAWN ONLY: move the native variant picker (<variant-radios>/<variant-selects>)
    // into the card, ABOVE the subscription selector — the Appstle widget lives in
    // <product-form> just below the card, so appending the picker as the card's
    // last child places it above that widget. We move the WHOLE custom element so
    // Dawn's own change handling (variant switch → price/buy-button re-render)
    // stays intact; the radios keep their `form="…"` attr so form submission is
    // unaffected by the move. Styling is CSS-only (.bogo-variant-radios) so it
    // survives Dawn re-rendering the picker's inner markup. Idempotent (guarded on
    // "not already in the card") and re-asserted from updateCardPrice below, so a
    // late/re-rendered picker still gets pulled in. Gated on isDawn — no-ops on
    // Luxora, whose native picker is the <product-variant> handled separately.
    // True when Appstle offers BOTH a subscription plan AND a one-time purchase.
    const hasOneTime = () => {
      const w = document.querySelector('#appstle_subscription_widget0');
      if (!w) return false;
      const rs = [...w.querySelectorAll('input[name="selling_plan"]')];
      return rs.some((r) => r.value === '') && rs.some((r) => r.value !== '');
    };

    const moveVariantPicker = () => {
      if (!isDawn) return;
      if (!card.contains(header)) return; // keep the picker below the header
      const picker = document.querySelector('variant-radios, variant-selects');
      if (!picker) return;
      picker.classList.add('bogo-variant-radios');
      if (!picker.closest('#lf-card')) card.appendChild(picker);
      // A single-option variant carries no real choice → hide the pointless
      // one-option field (the <select> stays in the DOM for form submission).
      // EXCEPT when a subscribe/one-time toggle is present: the subscribe tab shows
      // it as a dropdown and buildPlanToggle's applyState controls its visibility.
      const dsel = picker.querySelector('select');
      if (dsel && dsel.options.length <= 1 && !hasOneTime()) {
        const field = dsel.closest('.product-form__input') || picker;
        field.style.setProperty('display', 'none', 'important');
      }
    };

    // Style the accelerated-checkout ("Buy it now") button by selection state.
    // Done with inline `!important` rather than CSS because Shopify's own
    // cross-origin accelerated-checkout stylesheet sets the button background at
    // a specificity no class-based rule of ours can beat (our CSS wins for e.g.
    // border-radius but loses for background); inline beats it.
    //   • elife (Dawn): outlined — white fill / black text / black border by
    //     default, flipping to solid black / white text when single is
    //     selected. The border stays on both states so the button doesn't
    //     resize between them (box-sizing is border-box).
    //   • Luxora: solid black / white text when single is selected, otherwise
    //     cleared back to the theme's own button style.
    const syncSingleBuyButton = () => {
      const on = !!card.querySelector('.bogo-radio')?.checked;
      const b = document.querySelector('.shopify-payment-button__button');
      if (!b) return;
      if (isDawn) {
        b.style.setProperty('border', '1px solid #000', 'important');
        b.style.setProperty('background-color', on ? '#000' : '#fff', 'important');
        b.style.setProperty('color', on ? '#fff' : '#000', 'important');
      } else if (on) {
        b.style.setProperty('background-color', '#000', 'important');
        b.style.setProperty('color', '#fff', 'important');
      } else {
        b.style.removeProperty('background-color');
        b.style.removeProperty('color');
      }
    };

    // Idempotent: only writes on a real change, so observing an ancestor that
    // contains this card can't loop.
    const updateCardPrice = () => {
      trimAppstleWidget(); // re-assert the trim in case Appstle re-rendered (Dawn)
      moveVariantPicker(); // re-assert the picker move in case Dawn re-rendered it (Dawn)
      buildPlanToggle(); // Dawn + one-time: (re)build/sync the subscribe/one-time pills
      syncSingleBuyButton(); // re-assert in case Shopify re-rendered the button
      const p = readPrices();
      if (!p || !p.current) {
        console.log('[single] updateCardPrice: no current price read', p);
        return;
      }
      if (priceCopy.textContent !== p.current) priceCopy.innerHTML = __bogoYen(p.current);

      if (p.struck && p.struck !== p.current) {
        if (wasCopy.textContent !== p.struck) wasCopy.innerHTML = __bogoYen(p.struck);
        if (wasCopy.style.display !== '') wasCopy.style.display = '';
      } else if (wasCopy.style.display !== 'none') {
        wasCopy.style.display = 'none';
      }

      if (p.savings) {
        const txt = T.save(Math.round(p.savings).toLocaleString('ja-JP'));
        if (savingsEl.textContent !== txt) savingsEl.innerHTML = __bogoYen(txt);
        if (savingsEl.style.display !== '') savingsEl.style.display = '';
      } else if (savingsEl.style.display !== 'none') {
        savingsEl.style.display = 'none';
      }
    };

    // One-time products (BOTH stores): some products offer a subscription plan AND
    // a one-time ("一回購入", buy without subscription) purchase, chosen via two
    // Appstle radios. A radio here would nest inside the card's own 単品/セット radio
    // (radio-in-radio), so we replace Appstle's two radios with a segmented pill
    // toggle. The pills drive the underlying (hidden) radios — which set the cart's
    // selling_plan — and show/hide the subscribe-only details (plan/variant selectors
    // + description). No-ops on subscription-only products (no one-time radio). The
    // Appstle standalone widget + #lf-card exist on both themes, so this is
    // theme-agnostic. Idempotent; re-asserted from updateCardPrice.
    function buildPlanToggle() {
      const widget = document.querySelector('#appstle_subscription_widget0');
      if (!widget) return;
      const radios = [...widget.querySelectorAll('input[type=radio][name="selling_plan"]')];
      const subRadio = radios.find((r) => r.value !== '');
      const oneRadio = radios.find((r) => r.value === '');
      if (!subRadio || !oneRadio) return; // subscription-only → leave the plan dropdown as-is

      // Hide Appstle's native radio rows — the pills replace them.
      widget.querySelector('.appstle_subscription_radio_wrapper')?.style.setProperty('display', 'none', 'important');
      widget.querySelector('.appstle_one_time_details_wrapper')?.style.setProperty('display', 'none', 'important');

      // On the subscribe tab, show the subscription (plan/frequency) selector as a
      // real dropdown even when it has a single option, and hide the single-option
      // text Appstle would otherwise show in its place. (Only the grid's own plan
      // label is hidden — descriptions may reuse .appstle_single_option_text.)
      widget.querySelector('.appstle_custom_select')?.style.setProperty('display', 'block', 'important');
      widget.querySelector('.appstle_select')?.style.setProperty('display', 'block', 'important');
      widget.querySelectorAll('.appstle_subscribe_option > .appstle_single_option_text')
        .forEach((el) => el.style.setProperty('display', 'none', 'important'));

      const subDetails =
        widget.querySelector('.appstle_subscription_wrapper_option.appstle_include_dropdown .appstle_subscribe_option') ||
        widget.querySelector('.appstle_subscribe_option');
      const priceOf = (el) => (el && (el.textContent.match(/¥[\d,]+/) || [])[0]) || '';
      const subPrice = priceOf(widget.querySelector('.appstle_subscription_radio_wrapper .appstle_subscription_amount'));
      const onePrice = priceOf(widget.querySelector('.appstle_one_time_price_wrapper .appstle_subscription_amount'));

      // The pills live in #lf-card (not the widget), so look for them there —
      // otherwise the "already built?" check never matches and rebuilds them on
      // every tick (infinite duplication).
      let toggle = card.querySelector('.bogo-plan-toggle');
      if (!toggle) {
        const mk = (labelText, price, plan) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'bogo-plan-pill';
          b.dataset.plan = plan;
          b.innerHTML = `<span class="bogo-pill-label">${labelText}</span><span class="bogo-pill-price">${__bogoYen(price)}</span>`;
          return b;
        };
        toggle = document.createElement('div');
        toggle.className = 'bogo-plan-toggle';
        toggle.append(mk(T.subscribe, subPrice, 'sub'), mk(T.oneTime, onePrice, 'one'));
        // Place the pills in our own #lf-card (right after the header), NOT inside
        // Appstle's widget — keeps them out of Appstle's re-rendered subtree. The
        // variant picker (also in #lf-card, appended by moveVariantPicker) ends up
        // just below them; the subscription selector stays in the form beneath.
        header.after(toggle);

        // Luxora only: a one-time checkout button. Luxora's CTA is Shopify's
        // accelerated "Buy it now", which forces a selling plan for a subscription
        // product (so one-time would check out as a subscription). This button
        // instead adds the selected variant with NO selling plan, then goes straight
        // to checkout — the reliable one-time path (same as elife's add-to-cart).
        // Created once; applyState shows it only on the one-time tab. Skipped on Dawn
        // (no accelerated button — add-to-cart already honors one-time there).
        const accWrapInit = document.querySelector('.shopify-payment-button');
        if (accWrapInit && !document.getElementById('bogo-onetime-cta')) {
          const otBtn = document.createElement('button');
          otBtn.id = 'bogo-onetime-cta';
          otBtn.type = 'button';
          otBtn.className = 'bogo-onetime-cta';
          otBtn.textContent = accWrapInit.querySelector('.shopify-payment-button__button')?.textContent?.trim() || '今すぐ購入';
          otBtn.style.display = 'none';
          accWrapInit.after(otBtn);
          otBtn.addEventListener('click', async () => {
            if (otBtn.dataset.busy) return;
            otBtn.dataset.busy = '1';
            try {
              const variantId = document.querySelector('product-form form [name="id"], form[action*="/cart/add"] [name="id"]')?.value;
              if (variantId) {
                await fetch('/cart/add.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
                });
              }
              window.location.href = '/checkout';
            } catch (e) {
              delete otBtn.dataset.busy;
              console.error('[bogo] one-time checkout failed', e);
            }
          });
        }

        const applyState = () => {
          const checked = widget.querySelector('input[name="selling_plan"]:checked');
          const oneSelected = !!checked && checked.value === '';
          toggle.querySelector('.bogo-plan-pill[data-plan="sub"]').setAttribute('aria-pressed', String(!oneSelected));
          toggle.querySelector('.bogo-plan-pill[data-plan="one"]').setAttribute('aria-pressed', String(oneSelected));
          // Subscribe-only content: the plan/frequency selector + description, and
          // the native variant field in the card (Dawn: .bogo-variant-radios;
          // Luxora: .product-variant) — hidden on the one-time tab, shown on subscribe.
          if (subDetails) subDetails.style.setProperty('display', oneSelected ? 'none' : 'block', 'important');
          card.querySelectorAll('.bogo-variant-radios, .product-variant').forEach((vf) => {
            if (oneSelected) vf.style.setProperty('display', 'none', 'important');
            else vf.style.removeProperty('display');
          });
          // Explicitly toggle the selected-plan description too: on Dawn it sits
          // inside subDetails (so it hides with it), but on Luxora it's elsewhere in
          // the widget and would otherwise linger on the one-time tab.
          const nd = widget.querySelector('.appstleSelectedSellingPlanOptionDescription');
          if (nd) {
            if (oneSelected) nd.style.setProperty('display', 'none', 'important');
            else nd.style.removeProperty('display');
          }
          // Luxora CTA swap: the accelerated "Buy it now" can't do one-time (it
          // defaults to a plan for a subscription product), so on the one-time tab
          // hide it and show our own one-time checkout button (see below). Subscribe
          // keeps the accelerated button. No-op on Dawn (uses add-to-cart, no accel).
          const accWrap = document.querySelector('.shopify-payment-button');
          const otCta = document.getElementById('bogo-onetime-cta');
          if (accWrap && otCta) {
            accWrap.style.setProperty('display', oneSelected ? 'none' : '', 'important');
            otCta.style.display = oneSelected ? 'block' : 'none';
          }
        };
        toggle.querySelector('.bogo-plan-pill[data-plan="sub"]').addEventListener('click', () => { subRadio.click(); applyState(); updateCardPrice(); });
        toggle.querySelector('.bogo-plan-pill[data-plan="one"]').addEventListener('click', () => { oneRadio.click(); applyState(); updateCardPrice(); });
        toggle._applyState = applyState;
      } else {
        // Refresh the pills' prices in case Appstle re-rendered them.
        const setP = (plan, price) => {
          const el = toggle.querySelector(`.bogo-plan-pill[data-plan="${plan}"] .bogo-pill-price`);
          if (el && price && el.textContent !== price) el.innerHTML = __bogoYen(price);
        };
        setP('sub', subPrice);
        setP('one', onePrice);
      }
      if (toggle._applyState) toggle._applyState(); // keep pill state in sync with the checked radio
    }

    updateCardPrice();

    // The theme/app rewrites the price nodes on plan/variant change. Observe a
    // stable page root (Luxora: product-page; Dawn: product-info) and re-read;
    // updateCardPrice is idempotent so this can't loop. Also bind the native
    // picker's own change (Luxora) for an immediate update.
    let refreshTimer = null;
    const schedule = () => { clearTimeout(refreshTimer); refreshTimer = setTimeout(updateCardPrice, 60); };
    if (variant) variant.addEventListener('change', schedule);
    const priceRoot =
      card.closest('product-page') || card.closest('product-info') ||
      document.querySelector('product-page, product-info') || document.body;
    new MutationObserver(schedule).observe(priceRoot, { childList: true, subtree: true, characterData: true });

    card.appendChild(header);
    moveVariantPicker(); // Dawn: pull the native variant picker into the card (above the subscription selector)

    // LUXORA ONLY: move the native picker into the card. Skipped on Dawn
    // (variant is null). If the variant <select> has only one option, show that
    // option as static text instead of a pointless single-option dropdown — the
    // <select> stays in the DOM (hidden) so the theme's variant/form logic still
    // reads its value.
    if (variant) {
      card.appendChild(variant);
      const vsel = variant.tagName === 'SELECT' ? variant : variant.querySelector('select');
      if (vsel && vsel.options.length <= 1) {
        vsel.style.display = 'none';
        // Single option: hide the variant name label too (inline !important beats
        // the #lf-card .product-variant__name { display:block !important } rule),
        // so only the value text shows.
        const label = variant.querySelector('.product-variant__name');
        if (label) label.style.setProperty('display', 'none', 'important');
        const txt = document.createElement('div');
        txt.className = 'custom-selling-plan-text';
        txt.textContent = vsel.options[0]?.text || '';
        vsel.after(txt);
      }
    }

    // Selected selling-plan description: on BOTH themes we surface Appstle's own
    // standalone widget, which renders a live description below the plan dropdown
    // (un-hidden + styled via CSS) — so nothing is registered here. (The bundle
    // rows register their own copies.)
    __bogoRefreshPlanDescs();
    __bogoStartDescObserver(); // PERF: attach the plan-desc observer only now (post-build), scoped

    // Clicking anywhere in the card, its enclosing <product-variants> (Luxora),
    // the sibling .product-actions (Luxora buttons), or <product-form> (Dawn
    // buttons) selects the single option. Each target is absent-safe.
    const selectSingle = () => {
      card.querySelector('.bogo-radio').checked = true;
      syncSingleBuyButton();
    };
    const variantsEl = card.closest('product-variants');
    const actionsEl =
      variantsEl?.parentElement?.querySelector('.product-actions') ||
      document.querySelector('.product-actions');
    [card, variantsEl, actionsEl, form].forEach((el) => el && el.addEventListener('click', selectSingle));

    // Selecting the OTHER (bundle) option unchecks the single radio, but that's
    // handled in the bundle block. Re-sync the button on any document click too
    // (fires after the widget's own click handlers during bubbling), so picking
    // the bundle clears the single button's black fill.
    document.addEventListener('click', syncSingleBuyButton);

    // Initial state (bundle is the default selection → button stays theme-styled).
    syncSingleBuyButton();

    console.log('[single] buildCard: built ✓');
    window.__bogoReady();
    return true;
  }

  if (buildCard()) {
    return;
  }

  let debounceTimer = null;
  let giveUpTimer = null;
  const obs = new MutationObserver(() => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      if (buildCard()) {
        obs.disconnect();
        clearTimeout(giveUpTimer);
      }
    }, 250);
  });
  // PERF: scope to the product area (see the bundle block's observer note).
  obs.observe(
    document.querySelector('product-info, product-page') ||
      document.querySelector('main, #MainContent') ||
      document.documentElement,
    { childList: true, subtree: true },
  );

  // On a template where the single card never builds, count the block as
  // "done, nothing to show" so the reveal isn't blocked waiting on it.
  giveUpTimer = setTimeout(() => {
    console.warn('[single] GIVE UP after 10s — no single card on this template, marking block done');
    obs.disconnect();
    window.__bogoReady();
  }, 10000);

  // Fast path for bundle-only pages (no single-purchase area, e.g. a Mounjaro
  // bundle landing page): Dawn renders <product-form> server-side for any
  // purchasable product, so once the DOM is parsed, no <product-form> AND no
  // price block means there's no single card to build. Mark this block done
  // immediately so the reveal — which waits on BOTH blocks — isn't held for the
  // full 10s give-up. Guarded so it never fires once the card has built.
  const bailIfNoSingleArea = () => {
    if (document.getElementById('lf-card')) return;
    if (!document.querySelector('product-form, .product-price, .elife-price-wrapper')) {
      console.warn('[single] no single-purchase area on this page → marking block done');
      obs.disconnect();
      clearTimeout(giveUpTimer);
      window.__bogoReady();
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bailIfNoSingleArea, { once: true });
  } else {
    bailIfNoSingleArea();
  }
})();